import { useLazyQuery, useMutation } from "@apollo/client";
import React, { useContext, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import Autocomplete from "react-autocomplete";

import { SEARCH_CATEGORIES, ADD_CATEGORY } from "../queries";
import { ActionType } from "./productContext/actions";
import { ProductContext } from "./productContext/context";

const menuStyle = {
  borderRadius: "3px",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.1)",
  background: "rgba(255, 255, 255, 1)",
  padding: "2px 0",
  fontSize: "90%",
  position: "fixed",
  overflow: "auto",
  maxHeight: "50%",
  zIndex: 5000,
};

type CategoryItem = { id: string; name: string };

const NEW_CATEGORY_ID = "__new__";

const CategoriesSearch: React.FC = () => {
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<CategoryItem[]>([]);
  const [searchCategories, { data }] = useLazyQuery(SEARCH_CATEGORIES);
  const [addCategory, { loading: creating }] = useMutation(ADD_CATEGORY);
  const { dispatch, state } = useContext(ProductContext);

  useEffect(() => {
    if (data) {
      setSearchResults(data.searchCategories);
    }
  }, [data]);

  useEffect(() => {
    if (state.category) {
      setSearch(state.category.name);
    }
  }, [state.category]);

  const set = (text: string) => {
    setSearch(text);
    getCategories();
  };

  const setCategory = async (category: CategoryItem) => {
    if (category.id === NEW_CATEGORY_ID) {
      try {
        const { data } = await addCategory({
          variables: { plural: search.trim(), singular: search.trim() },
        });
        const created = data?.addCategory;
        if (created) {
          dispatch({
            type: ActionType.SetCategory,
            payload: { id: created.id, name: created.name },
          });
        }
      } catch (err) {
        console.error("Error creating category:", err);
        alert("Error al crear la categoría. Intenta de nuevo.");
      }
      return;
    }
    dispatch({
      type: ActionType.SetCategory,
      payload: { id: category.id, name: category.name },
    });
  };

  const getCategories = useDebouncedCallback(async () => {
    if (search && search.length > 0) {
      searchCategories({ variables: { search } });
    }
  }, 400);

  const items: CategoryItem[] = [...searchResults];
  const trimmed = search.trim();
  const exactMatch = searchResults.some(
    (c) => c.name.toLowerCase() === trimmed.toLowerCase()
  );
  if (trimmed.length > 0 && !exactMatch) {
    items.push({ id: NEW_CATEGORY_ID, name: trimmed });
  }

  return (
    <div className="form-group p_relative" id="ingredients_div">
      <label htmlFor="product_ingredients">
        Tipo de producto <span className="text-danger">*</span>
      </label>
      <small id="emailHelp" className="form-text text-muted">
        Selecciona o crea un tipo de producto
      </small>
      <Autocomplete
        getItemValue={(item: CategoryItem) => item.name}
        items={items}
        value={search}
        onChange={(_, value) => set(value)}
        onSelect={(_, category: CategoryItem) => setCategory(category)}
        //@ts-ignore
        menuStyle={menuStyle}
        renderInput={(props) => (
          <input
            type="text"
            className="form-control dropdown-toggle text-capitalize"
            id="product_ingredients"
            placeholder="Buscar tipos de producto"
            disabled={creating}
            {...props}
          />
        )}
        renderItem={(category: CategoryItem) => (
          <div
            key={`ingredient-${category.id}`}
            className={`dropdown-item py-3 text-capitalize${
              category.id === NEW_CATEGORY_ID ? " text-success" : ""
            }`}
          >
            {category.id === NEW_CATEGORY_ID ? (
              <>
                <i
                  className="fa fa-plus-circle text-success"
                  aria-hidden="true"
                ></i>{" "}
                Crear: <strong>{category.name}</strong>
              </>
            ) : (
              <>
                <i
                  className="fa fa-plus text-primary"
                  aria-hidden="true"
                ></i>{" "}
                {category.name}
              </>
            )}
          </div>
        )}
      />
    </div>
  );
};

export default CategoriesSearch;
